import { logUsage } from 'owa-analytics';
import { assertNever } from 'owa-assert';
import {
    AttachOrSharePreference,
    FilePickerAction,
    FilePickerUserConfiguration,
    FileProviderItemViewState,
    getDefaultFileProvider,
    SelectedFilesSource,
} from 'owa-filepicker';
import {
    ClientOnlyFileProviderType,
    FileProviderViewState,
    FileProviderViewType,
    getProviderBrandName,
    ProviderLoadStateOption,
} from 'owa-fileprovider-store';
import { getFileProviders } from 'owa-fileprovider-store/lib/selectors/getFileProviders';
import { getProviderLoadState } from 'owa-fileprovider-store/lib/selectors/getProviderLoadState';
import loc from 'owa-localize';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { select } from 'satcheljs/lib/legacy';
import setComposeUniqueState from './actions/setComposeUniqueState';
import {
    updateSupportedFilePickerActionsForLocalFiles,
    updateSupportedFilePickerActionsForProviderFiles,
} from './actions/updateSupportedFilePickerActions';
import {
    ActionsPanel_CheckboxTextFromGroups,
    ActionsPanel_CheckboxTextFromOneDrive,
} from './actionsPanel.locstring.json';
import showFilePickerActionsPanel from './components/ActionsPanel/FilePickerActionsPanelView';
import { getFilePickerStore } from './store/filePickerStore';
import FilePickerPanelViewState from './store/schema/FilePickerPanelViewState';
import { ComposeUniqueState } from './store/schema/FilePickerUploadConfiguration';
import getUserPreferredFilePickerAction from './utils/getUserPreferredFilePickerAction';

// TODO: VSO 30585 decouple the ActionPanel and the FilePicker
function getFileProviderViewState(providerType: FileProviderViewType): FileProviderViewState {
    if (getProviderLoadState() !== ProviderLoadStateOption.Loaded) {
        throw new Error(
            'No file providers loaded. Please load the file providers before opening the actions panel'
        );
    }

    const [providerViewState] = getFileProviders().filter(
        provider => provider.viewType === providerType
    );

    return providerViewState;
}

function updateSupportedFilePickerActions(
    selectedFileSource: SelectedFilesSource,
    files: File[] | FileProviderItemViewState[],
    maxAllowedLocalFileSize: number,
    viewState: FilePickerPanelViewState,
    supportsInsertLink: boolean
) {
    if (selectedFileSource === SelectedFilesSource.ProviderFiles) {
        const selectedFileProviderViewState = getFileProviderViewState(
            viewState.selectedFileProvider.viewType
        );
        if (!selectedFileProviderViewState) {
            throw new Error(
                `File provider ${viewState.selectedFileProvider.viewType} is not available`
            );
        }

        updateSupportedFilePickerActionsForProviderFiles(
            <FileProviderItemViewState[]>files,
            maxAllowedLocalFileSize,
            selectedFileProviderViewState,
            viewState,
            supportsInsertLink
        );
    } else if (selectedFileSource === SelectedFilesSource.LocalComputerFiles) {
        updateSupportedFilePickerActionsForLocalFiles(
            <File[]>files,
            maxAllowedLocalFileSize,
            viewState
        );
    } else {
        assertNever(selectedFileSource);
    }
}

/**
 * There are two things that can happen when we intent to open the action panel.
 * 1- Open the action panel and ask the user what they want to do e.g. Attach, Share, Insert
 * 2- If the user has already selected to always attach/share for cloud or local files then
 *    we don't need to open the action panel but just execute the user preferred action.
 *
 * To differentiate between these two things we have this enum and corresponding types
 * so we can choose between them and it will have extra data related to it.
 */
const enum UserChoiceValueType {
    Info,
    Action,
}

/**
 * This type is if we want to show the action panel to user. It contains other information i.e.
 * the optional text to show the user for setting preference and corresponding setter
 */
interface UserChoiceInfo {
    type: UserChoiceValueType.Info;
    rememberUserChoiceText: string | null;
    userChoiceSetter: (value: AttachOrSharePreference) => void | null;
}

/**
 * This type is if we don't want to open the action panel but instead directly execute the specified
 * action e.g. Attach, Share etc.
 */
interface UserChoiceAction {
    type: UserChoiceValueType.Action;
    action: FilePickerAction;
}

/**
 * This is an empty user choice info action which would show the action panel but it will not have any ability
 * to select the preferred action
 */
const emptyUserChoiceInfo: UserChoiceInfo = <UserChoiceInfo>Object.freeze({
    type: UserChoiceValueType.Info,
    rememberUserChoiceText: null,
    userChoiceSetter: null,
});

type UserChoice = UserChoiceAction | UserChoiceInfo;

const enum UserChoiceToSelect {
    EmptyUserChoiceInfo,
    UserChoiceInfo,
    UserChoiceAction,
}

function getUserChoiceForProviderFiles(
    selectedFileProviderViewState: FileProviderViewState,
    allowedActions: FilePickerAction[],
    userConfiguration?: FilePickerUserConfiguration
): UserChoice {
    if (!selectedFileProviderViewState) {
        throw new Error('File provider is not available');
    }

    const isGroupsProvider =
        selectedFileProviderViewState.viewType === ClientOnlyFileProviderType.Groups;
    const isPrimaryProvider = selectedFileProviderViewState.isPrimaryProvider;

    let preferredAction: FilePickerAction | null = null;
    if (isPrimaryProvider) {
        preferredAction = userConfiguration
            ? getUserPreferredFilePickerAction(userConfiguration.attachOrShareCloudFiles)
            : null;
    } else if (isGroupsProvider) {
        preferredAction = userConfiguration
            ? getUserPreferredFilePickerAction(userConfiguration.attachOrShareGroupsFiles)
            : null;
    }

    const isPreferredActionAllowed = allowedActions.some(action => action === preferredAction);

    /**
     * We have the following scenarios
     * 1- The user has not selected a preferred action then do the following
     *      a- If the selected provider is primary provider then it should open action panel with
     *         the ability to select preferred action.
     *      b- If the selected provider is not primary provider then it should open action panel
     *         without the ability to select preferred action. As we only allow to set preferred action for primary providers.
     * 2- The user has selected the preferred action for cloud file in that case the following will happen
     *      a- If the user preferred action is not allowed or the provider is a third party provider
     *         then open action panel without the ability to select preferred action
     *      b- If not any of the above condition then execute the user preferred action
     */

    // This takes care of scenario 2(b)
    let userChoiceToSelect: UserChoiceToSelect = UserChoiceToSelect.UserChoiceAction;

    if (preferredAction === null) {
        // This takes care of scenario 1(b)
        userChoiceToSelect = UserChoiceToSelect.EmptyUserChoiceInfo;

        if (userConfiguration && (isPrimaryProvider || isGroupsProvider)) {
            // This takes care of scenario 1(a)
            userChoiceToSelect = UserChoiceToSelect.UserChoiceInfo;
        }
    } else if (!isPreferredActionAllowed) {
        // This takes care of scenario 2(a)
        userChoiceToSelect = UserChoiceToSelect.EmptyUserChoiceInfo;
    }

    switch (userChoiceToSelect) {
        case UserChoiceToSelect.EmptyUserChoiceInfo:
            return { ...emptyUserChoiceInfo };
        case UserChoiceToSelect.UserChoiceInfo:
            return isGroupsProvider
                ? getGroupsUserChoiceInfo(userConfiguration)
                : getOneDriveUserChoiceInfo(userConfiguration);
        case UserChoiceToSelect.UserChoiceAction:
            return {
                type: UserChoiceValueType.Action,
                action: preferredAction,
            };
        default:
            return assertNever(userChoiceToSelect);
    }
}

function getGroupsUserChoiceInfo(userConfiguration: FilePickerUserConfiguration): UserChoiceInfo {
    return {
        type: UserChoiceValueType.Info,
        rememberUserChoiceText: loc(ActionsPanel_CheckboxTextFromGroups),
        userChoiceSetter: userConfiguration.attachOrShareGroupsFiles.valueSetter,
    };
}

function getOneDriveUserChoiceInfo(userConfiguration: FilePickerUserConfiguration): UserChoiceInfo {
    return {
        type: UserChoiceValueType.Info,
        rememberUserChoiceText: loc(ActionsPanel_CheckboxTextFromOneDrive),
        userChoiceSetter: userConfiguration.attachOrShareCloudFiles.valueSetter,
    };
}

function getUserChoice(
    allowedActions: FilePickerAction[],
    selectedFileSource: SelectedFilesSource,
    selectedFileProviderViewState: FileProviderViewState,
    userConfiguration?: FilePickerUserConfiguration
): UserChoice {
    switch (selectedFileSource) {
        // We do not support user choice for local computer files in react
        case SelectedFilesSource.LocalComputerFiles:
            return emptyUserChoiceInfo;
        case SelectedFilesSource.ProviderFiles:
            return getUserChoiceForProviderFiles(
                selectedFileProviderViewState,
                allowedActions,
                userConfiguration
            );
        default:
            return assertNever(selectedFileSource);
    }
}

function getForcedFilePickerAction(): FilePickerAction | null {
    const configuration = getUserConfiguration();

    if (
        (!configuration.AttachmentPolicy.ReferenceAttachmentsEnabled ||
            (getProviderLoadState() === ProviderLoadStateOption.Loaded &&
                getFileProviders().length === 0)) &&
        configuration.AttachmentPolicy.ClassicAttachmentsEnabled
    ) {
        // If the reference attachment is disabled or no providers are available but classic
        // attachment is enabled then force the Attach action
        return FilePickerAction.Attach;
    } else if (
        !configuration.AttachmentPolicy.ClassicAttachmentsEnabled &&
        configuration.AttachmentPolicy.ReferenceAttachmentsEnabled
    ) {
        // If the classic attachment is disabled but reference attachment is enabled
        // then force the share action
        return FilePickerAction.Share;
    }

    return null;
}

function doesPolicyAllowAction(action: FilePickerAction): boolean {
    const configuration = getUserConfiguration();
    switch (action) {
        case FilePickerAction.Attach:
        case FilePickerAction.Insert:
            return configuration.AttachmentPolicy.ClassicAttachmentsEnabled;
        case FilePickerAction.Share:
        case FilePickerAction.InsertAsLink:
            return configuration.AttachmentPolicy.ReferenceAttachmentsEnabled;
        case FilePickerAction.AttachAndShare:
            return (
                configuration.AttachmentPolicy.ClassicAttachmentsEnabled &&
                configuration.AttachmentPolicy.ReferenceAttachmentsEnabled
            );
        default:
            return assertNever(action);
    }
}

export interface ActionsPanelState {
    filePickerPanelViewState: FilePickerPanelViewState;
}

export function logSecondPanelUsage(
    selectedFileProviderType: AttachmentDataProviderType,
    isPreferredActionSet: boolean,
    action: FilePickerAction
) {
    if (
        selectedFileProviderType == AttachmentDataProviderType.OneDrivePro ||
        (selectedFileProviderType == AttachmentDataProviderType.OneDriveConsumer && isConsumer())
    ) {
        logUsage(
            isPreferredActionSet ? 'SharingLinkWithoutSecondPanel' : 'SharingLinkWithSecondPanel',
            {},
            {
                cosmosOnlyData: JSON.stringify({
                    providerType: selectedFileProviderType,
                    userChoice: action,
                }),
            }
        );
    }
}

export default select<ActionsPanelState>({
    filePickerPanelViewState: () => getFilePickerStore().viewState,
})(function actionsPanel(
    files: File[] | FileProviderItemViewState[],
    maxAllowedLocalFileSize: number,
    selectedFileProviderType: AttachmentDataProviderType | null,
    isGroupsFileProvider: boolean,
    isMultipleSelection: boolean,
    selectedFileSource: SelectedFilesSource,
    targetWindow: Window,
    userConfiguration?: FilePickerUserConfiguration,
    composeUniqueState?: ComposeUniqueState,
    supportsInsertLink?: boolean,
    state?: ActionsPanelState
): Promise<FilePickerAction> {
    composeUniqueState && setComposeUniqueState(state.filePickerPanelViewState, composeUniqueState);
    return new Promise<FilePickerAction>((resolve, reject) => {
        const forcedAction = getForcedFilePickerAction();
        if (forcedAction !== null) {
            // Check if there is an action that we should force regardless of what user prefers.
            // If there is then resolve with that action
            resolve(forcedAction);
            return;
        }

        updateSupportedFilePickerActions(
            selectedFileSource,
            files,
            maxAllowedLocalFileSize,
            state.filePickerPanelViewState,
            supportsInsertLink
        );

        // The updateSupportedFilePickerActions will update the list of supported actions
        // hence we get them from the viewState after calling that action
        const {
            filePickerViewState: { supportedActions, supportedActionsMessages },
            selectedFileProvider,
        } = state.filePickerPanelViewState;

        const fileProvidersViewState = getFileProviders();
        const providerDisplayName =
            selectedFileSource === SelectedFilesSource.LocalComputerFiles
                ? getProviderBrandName(getDefaultFileProvider(fileProvidersViewState).type)
                : getProviderBrandName(selectedFileProviderType, isGroupsFileProvider);

        const userChoice: UserChoice = getUserChoice(
            supportedActions,
            selectedFileSource,
            selectedFileProvider,
            userConfiguration
        );

        if (
            userChoice.type === UserChoiceValueType.Action &&
            doesPolicyAllowAction(userChoice.action)
        ) {
            // If there is a user preferred action then perform that
            // preferred action only applies to ODB or ODC, we need to check if it is ODB or ODC. If ODB, log it; if ODC, make sure it is from consumer account, NOT from business account
            logSecondPanelUsage(selectedFileProviderType, true, userChoice.action);
            resolve(userChoice.action);
        } else {
            // from local computer to GDrive
            if (
                getUserConfiguration().SessionSettings.IsShadowMailbox &&
                selectedFileSource === SelectedFilesSource.LocalComputerFiles
            ) {
                resolve(FilePickerAction.Attach);
            } else {
                showFilePickerActionsPanel(
                    resolve,
                    reject,
                    state.filePickerPanelViewState.filePickerActionsViewState,
                    supportedActions,
                    supportedActionsMessages,
                    userChoice.type === UserChoiceValueType.Info
                        ? userChoice.rememberUserChoiceText
                        : emptyUserChoiceInfo.rememberUserChoiceText,
                    userChoice.type === UserChoiceValueType.Info
                        ? userChoice.userChoiceSetter
                        : emptyUserChoiceInfo.userChoiceSetter,
                    isMultipleSelection,
                    providerDisplayName,
                    selectedFileProviderType,
                    selectedFileSource,
                    targetWindow
                );
            }
        }
    });
});
